"use client";

import type React from "react";
import type { ToastOptions as BaseToastOptions } from "react-toastify/unstyled";
import { isLazyModuleLoaded, type LazyModule, loadLazyModule } from "src/lib/LazyModules";
import { ToastHelpLink, ToastIcon } from "./ToastIcon";

// The toast functions work from anywhere, but react-toastify and its container are a lazy module
// (ToastRuntime.ts, LAZY_MODULES.toasts), loaded when the page is idle or by the first toast.
// Calls made before it has loaded and the container has mounted wait in `pending` and run in order
// once it has.

export interface ToastOptions extends BaseToastOptions {}

type ToastRuntime = LazyModule<"toasts">;

/** The loaded runtime, once `Toasts` has mounted its container (`attachToastRuntime`). */
let runtime: ToastRuntime | undefined;
const pending: Array<(runtime: ToastRuntime) => void> = [];
const listeners = new Set<() => void>();

/** Loads the runtime (once); `Toasts` then mounts the container and runs the pending calls. */
export const loadToasts = () => {
  loadLazyModule("toasts").then(
    () => {
      for (const listener of listeners) listener();
    },
    // A failed load is retried by the next toast.
    () => {},
  );
};

const withToasts = (call: (runtime: ToastRuntime) => void) => {
  if (runtime) {
    call(runtime);
    return;
  }
  pending.push(call);
  loadToasts();
};

const toast = (message: React.ReactNode, options: ToastOptions) => {
  withToasts((loaded) => loaded.showToast(message, options));
};

export const infoToast = (message: string, options?: ToastOptions) => {
  toast(message, {
    autoClose: 2000,
    position: "bottom-center",
    closeButton: false,
    ...options,
    type: "info",
  });
};

export const successToast = (message: string, options?: ToastOptions) => {
  toast(message, {
    autoClose: 2000,
    position: "bottom-center",
    closeButton: false,
    ...options,
    type: "success",
  });
};

export const hideToast = (id: string) => {
  if (runtime) runtime.dismissToast(id);
  // Nothing is on screen yet; dismiss it after any toast still waiting to be shown.
  else if (pending.length) pending.push((loaded) => loaded.dismissToast(id));
};

export type ErrorToastOptions = Omit<ToastOptions, "type"> & {
  toastId?: string;
  showHelp?: boolean;
  error?: Error;
};

export const errorToast = (message: React.ReactNode, options?: ErrorToastOptions) => {
  toast(
    <div className={"flex min-w-0 flex-col flex-1 gap-1"}>
      {message}
      {options?.showHelp ? (
        <>
          {" "}
          <ToastHelpLink />
        </>
      ) : null}
    </div>,
    {
      autoClose: false,
      toastId:
        options?.toastId || (typeof message === "string" ? `error-${message}` : "error-toast"),
      ...options,
      icon: options?.icon || <ToastIcon type={"error"} />,
      type: "error",
      position: "bottom-center",
    },
  );
};

export const subscribeToToasts = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
export const isToastRuntimeLoaded = () => isLazyModuleLoaded("toasts");

/** Runs the pending calls on the runtime once `Toasts` has mounted its container; returns a detach. */
export const attachToastRuntime = (module: ToastRuntime) => {
  runtime = module;
  for (const call of pending.splice(0)) call(module);
  return () => {
    runtime = undefined;
  };
};
